/* JoinDealPage.jsx — calm operational welcome for transaction participants.
 *
 * This is NOT a generic auth signup. It's the moment a buyer / seller /
 * solicitor / broker joins a live property transaction. The page is
 * designed to feel like opening a Deal Room, not creating a SaaS account.
 *
 * Layout (mobile-first):
 *   • Compact header (logo + secure indicator)
 *   • Hero card — property + stage + inviter + role badge
 *   • Trust strip — participants already in the room (avatar stack)
 *   • Next-step hint — single calm sentence
 *   • Target dates + outstanding actions (only when present)
 *   • Compact form — full name (new only) + password
 *   • Sticky CTA at the bottom of the viewport on mobile
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Loader2,
  ArrowRight,
  ShieldCheck,
  AlertTriangle,
  Building2,
  Send,
  Calendar,
  Inbox,
  Users,
  Lock,
} from "lucide-react";
import { toast } from "sonner";

import { api, apiError } from "../../lib/api";
import { useAuth } from "../../data/authStore";
import { PremiumInput } from "../../components/brand/PremiumInput";

const useQuery = () => new URLSearchParams(useLocation().search);

const ROLE_ACCENT = {
  buyer: { label: "Buyer", line: "Welcome — this is your home-buying transaction." },
  seller: { label: "Seller", line: "Welcome — this is the sale of your property." },
  buyer_solicitor: { label: "Buyer's Solicitor", line: "Welcome — co-ordinating the buyer side." },
  seller_solicitor: { label: "Seller's Solicitor", line: "Welcome — co-ordinating the seller side." },
  solicitor: { label: "Solicitor", line: "Welcome — your legal workstream lives here." },
  broker: { label: "Mortgage Broker", line: "Welcome — your mortgage workstream lives here." },
};

const fmtTargetDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch (_) {
    return null;
  }
};

/* ─── Atoms ──────────────────────────────────────────────────────────── */
const Pill = ({ tone = "violet", children, icon: Icon, testid }) => {
  const tones = {
    violet: "gp-pill gp-pill-violet",
    green: "gp-pill gp-pill-green",
    slate: "gp-pill gp-pill-slate",
    amber: "gp-pill gp-pill-amber",
  };
  return (
    <span className={tones[tone] || tones.slate} data-testid={testid}>
      {Icon ? <Icon size={11} className="mr-1" /> : null}
      {children}
    </span>
  );
};

const AvatarStack = ({ participants }) => {
  if (!participants || participants.length === 0) return null;
  const visible = participants.slice(0, 5);
  return (
    <div className="flex -space-x-2" data-testid="join-deal-avatar-stack">
      {visible.map((p, i) => (
        <div
          key={`${p.full_name}-${i}`}
          className="w-8 h-8 rounded-full text-[11px] font-bold flex items-center justify-center text-white ring-2 ring-white"
          style={{
            background: `linear-gradient(135deg, hsl(${(i * 71) % 360}, 60%, 55%), hsl(${(i * 71 + 30) % 360}, 60%, 38%))`,
          }}
          title={`${p.full_name} · ${p.role_label}`}
        >
          {p.initials}
        </div>
      ))}
    </div>
  );
};

/* ─── Main page ──────────────────────────────────────────────────────── */
export default function JoinDealPage() {
  const q = useQuery();
  const token = q.get("token") || "";
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [fatal, setFatal] = useState("");

  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setFatal("This invite link is missing a token.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/api/deal-invites/${encodeURIComponent(token)}`);
        if (!cancelled) setInfo(data);
      } catch (err) {
        if (!cancelled) setFatal(apiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!info) return;
    if (!info.user_exists) {
      if (!fullName.trim()) return setError("Full name is required");
      if (!password || password.length < 8) return setError("Password must be at least 8 characters");
    } else if (!password || password.length < 8) {
      return setError("Password must be at least 8 characters");
    }
    setSubmitting(true);
    try {
      const body = { token };
      if (!info.user_exists) {
        body.full_name = fullName.trim();
        body.password = password;
      } else {
        body.password = password;
      }
      const { data } = await api.post(
        `/api/deal-invites/${encodeURIComponent(token)}/accept`,
        body,
      );
      await refresh?.();
      toast.success("You're in", {
        description: `Joined ${info.property_address} as ${info.role_label}.`,
      });
      const dealId = data.deal_id;
      nav(dealId ? `/deal-rooms/${dealId}` : "/today", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const targetExchange = useMemo(() => fmtTargetDate(info?.target_exchange), [info]);
  const targetCompletion = useMemo(() => fmtTargetDate(info?.target_completion), [info]);
  const roleAccent = ROLE_ACCENT[info?.role] || { label: info?.role_label || "Participant", line: "Welcome — this Deal Room is now live for you." };

  /* ─── States ──────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <Shell>
        <div className="gp-card p-8 text-center" data-testid="join-deal-loading">
          <Loader2 size={22} className="mx-auto animate-spin" style={{ color: "var(--gp-violet-deep)" }} />
          <p className="mt-3 text-[13px]" style={{ color: "var(--gp-text-muted)" }}>
            Verifying your invitation…
          </p>
        </div>
      </Shell>
    );
  }

  if (fatal || !info) {
    return (
      <Shell>
        <div
          className="gp-card p-6"
          data-testid="join-deal-error"
          style={{
            background: "var(--gp-rose-soft)",
            border: "1px solid var(--gp-rose-border)",
          }}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="shrink-0" style={{ color: "#B91C1C" }} />
            <div>
              <p className="text-[10px] uppercase tracking-[0.16em] font-bold" style={{ color: "#B91C1C" }}>
                Invite issue
              </p>
              <p className="mt-1.5 text-[13.5px] leading-snug" style={{ color: "#7F1D1D" }} data-testid="join-deal-fatal">
                {fatal || "This invite link is invalid or has expired."}
              </p>
              <Link
                to="/login"
                className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-semibold"
                style={{ color: "var(--gp-violet-deep)" }}
                data-testid="join-deal-to-login"
              >
                Sign in to ChainFlow <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  const inviter = info.inviter_name || info.agent_name;

  return (
    <Shell>
      {/* Welcome card — property + role + inviter */}
      <section className="gp-card p-5 sm:p-7" data-testid="join-deal-summary">
        <div className="gp-eyebrow mb-3 flex items-center gap-2">
          <span
            className="inline-block w-1.5 h-1.5 rounded-full cf-pulse"
            style={{ background: "var(--gp-violet)" }}
          />
          DEAL ROOM · TRANSACTION
        </div>
        <div className="flex items-start gap-3.5">
          <div
            className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center text-white"
            style={{
              background: "linear-gradient(135deg, #6D28D9 0%, #4F46E5 100%)",
              boxShadow: "0 10px 22px -10px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.22)",
            }}
          >
            <Building2 size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <h1
              className="font-display font-bold text-[22px] sm:text-[26px] leading-[1.08] tracking-tight"
              style={{ color: "var(--gp-text)" }}
              data-testid="join-deal-property"
            >
              {info.property_address}
            </h1>
            <p
              className="mt-1 text-[13px] sm:text-[13.5px]"
              style={{ color: "var(--gp-text-muted)" }}
            >
              {roleAccent.line}
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Pill tone="violet" icon={ShieldCheck} testid="join-deal-role">
            {info.role_label}
          </Pill>
          {info.stage_label ? (
            <Pill tone="slate" testid="join-deal-stage">
              Stage · {info.stage_label}
            </Pill>
          ) : null}
          {inviter ? (
            <span
              className="inline-flex items-center gap-1.5 text-[11.5px]"
              style={{ color: "var(--gp-text-subtle)" }}
              data-testid="join-deal-inviter"
            >
              Invited by <strong style={{ color: "var(--gp-text-muted)" }}>{inviter}</strong>
            </span>
          ) : null}
        </div>
      </section>

      {/* Trust strip — participants + outstanding + target dates */}
      {(info.participants_count > 0 ||
        info.outstanding_count > 0 ||
        targetExchange ||
        targetCompletion) && (
        <section
          className="gp-card p-5 sm:p-6 space-y-3.5"
          data-testid="join-deal-trust"
        >
          {info.participants_count > 0 && (
            <div className="flex items-center gap-3.5">
              <AvatarStack participants={info.participants_preview} />
              <div className="min-w-0">
                <p
                  className="text-[12.5px] font-semibold"
                  style={{ color: "var(--gp-text)" }}
                  data-testid="join-deal-participants-line"
                >
                  {info.participants_count} {info.participants_count === 1 ? "person" : "people"} already in this room
                </p>
                <p
                  className="text-[11.5px]"
                  style={{ color: "var(--gp-text-subtle)" }}
                >
                  {info.participants_preview
                    .slice(0, 3)
                    .map((p) => `${p.full_name} · ${p.role_label}`)
                    .join(" · ")}
                </p>
              </div>
            </div>
          )}
          {info.next_step_hint ? (
            <div
              className="rounded-xl p-3.5"
              style={{
                background: "var(--gp-violet-soft)",
                border: "1px solid var(--gp-violet-border)",
              }}
              data-testid="join-deal-next-step"
            >
              <p
                className="text-[10px] uppercase tracking-[0.16em] font-bold"
                style={{ color: "var(--gp-violet-deep)" }}
              >
                What happens next
              </p>
              <p
                className="mt-1 text-[12.5px] leading-snug"
                style={{ color: "var(--gp-text)" }}
              >
                {info.next_step_hint}
              </p>
            </div>
          ) : null}
          {(info.outstanding_count > 0 || targetExchange || targetCompletion) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
              {info.outstanding_count > 0 && (
                <div
                  className="flex items-start gap-2.5 p-3 rounded-lg"
                  style={{
                    background: "#FAFAFA",
                    border: "1px solid var(--gp-card-border)",
                  }}
                  data-testid="join-deal-outstanding"
                >
                  <Inbox size={14} style={{ color: "var(--gp-violet-deep)" }} className="mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.14em] font-bold" style={{ color: "var(--gp-text-subtle)" }}>
                      Awaiting
                    </p>
                    <p className="text-[12.5px] font-semibold" style={{ color: "var(--gp-text)" }}>
                      {info.outstanding_count} outstanding {info.outstanding_count === 1 ? "item" : "items"}
                    </p>
                  </div>
                </div>
              )}
              {targetExchange && (
                <div
                  className="flex items-start gap-2.5 p-3 rounded-lg"
                  style={{
                    background: "#FAFAFA",
                    border: "1px solid var(--gp-card-border)",
                  }}
                  data-testid="join-deal-target-exchange"
                >
                  <Calendar size={14} style={{ color: "var(--gp-violet-deep)" }} className="mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.14em] font-bold" style={{ color: "var(--gp-text-subtle)" }}>
                      Target exchange
                    </p>
                    <p className="text-[12.5px] font-semibold" style={{ color: "var(--gp-text)" }}>
                      {targetExchange}
                    </p>
                  </div>
                </div>
              )}
              {targetCompletion && (
                <div
                  className="flex items-start gap-2.5 p-3 rounded-lg"
                  style={{
                    background: "#FAFAFA",
                    border: "1px solid var(--gp-card-border)",
                  }}
                  data-testid="join-deal-target-completion"
                >
                  <Calendar size={14} style={{ color: "var(--gp-violet-deep)" }} className="mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.14em] font-bold" style={{ color: "var(--gp-text-subtle)" }}>
                      Target completion
                    </p>
                    <p className="text-[12.5px] font-semibold" style={{ color: "var(--gp-text)" }}>
                      {targetCompletion}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Compact form */}
      <form onSubmit={submit} data-testid="join-deal-form" noValidate className="gp-card p-5 sm:p-6">
        <p className="gp-section-label mb-3 flex items-center gap-1.5">
          <Lock size={11} /> Secure access
        </p>
        <div className="space-y-3.5">
          <div>
            <label className="text-[11.5px] font-semibold" style={{ color: "var(--gp-text-muted)" }}>
              Email
            </label>
            <div className="mt-1">
              <PremiumInput
                type="email"
                value={info.email}
                onChange={() => {}}
                disabled
                readOnly
                size="lg"
                testid="join-deal-email"
              />
            </div>
          </div>
          {!info.user_exists ? (
            <div>
              <label className="text-[11.5px] font-semibold" style={{ color: "var(--gp-text-muted)" }}>
                Full name
              </label>
              <div className="mt-1">
                <PremiumInput
                  type="text"
                  autoComplete="name"
                  size="lg"
                  value={fullName}
                  onChange={setFullName}
                  placeholder="Your full name"
                  testid="join-deal-full-name"
                  autoFocus
                />
              </div>
            </div>
          ) : null}
          <div>
            <label className="text-[11.5px] font-semibold" style={{ color: "var(--gp-text-muted)" }}>
              {info.user_exists ? "Confirm your password" : "Create a password"}
            </label>
            <div className="mt-1">
              <PremiumInput
                type="password"
                autoComplete={info.user_exists ? "current-password" : "new-password"}
                size="lg"
                value={password}
                onChange={setPassword}
                placeholder="At least 8 characters"
                testid="join-deal-password"
                autoFocus={info.user_exists}
              />
            </div>
          </div>
          {error ? (
            <p className="text-[12px]" style={{ color: "#B91C1C" }} data-testid="join-deal-error-msg">
              {error}
            </p>
          ) : null}
        </div>
        {/* Inline submit on desktop. Sticky submit on mobile (separate). */}
        <button
          type="submit"
          disabled={submitting}
          className="hidden sm:inline-flex mt-5 w-full items-center justify-center gap-2 h-12 px-5 rounded-xl text-white text-[13.5px] font-bold transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:translate-y-0 disabled:cursor-not-allowed gp-btn-primary"
          data-testid="join-deal-submit"
        >
          {submitting ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Joining…
            </>
          ) : (
            <>
              Enter the Deal Room <ArrowRight size={14} />
            </>
          )}
        </button>
      </form>

      {/* Mobile sticky submit — outside the form so the form layout
          stays clean. Uses a small portal-less fixed footer. The
          inner button submits the form via `form` attribute. */}
      <div
        className="sm:hidden fixed left-0 right-0 bottom-0 z-40 px-4 pt-3"
        style={{
          background: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(14px) saturate(140%)",
          borderTop: "1px solid var(--gp-card-border)",
          paddingBottom: "max(16px, env(safe-area-inset-bottom, 16px))",
        }}
        data-testid="join-deal-mobile-cta"
      >
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 h-12 px-5 rounded-xl text-white text-[13.5px] font-bold gp-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="join-deal-submit-mobile"
        >
          {submitting ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Joining…
            </>
          ) : (
            <>
              Enter the Deal Room <ArrowRight size={14} />
            </>
          )}
        </button>
      </div>

      {/* Footer — quiet trust line */}
      <p
        className="mt-2 text-center text-[11.5px] flex items-center justify-center gap-1.5 pb-24 sm:pb-0"
        style={{ color: "var(--gp-text-subtle)" }}
      >
        <Lock size={11} /> Secure access · No card required · Email already verified
      </p>
      <p
        className="text-center text-[11.5px]"
        style={{ color: "var(--gp-text-subtle)" }}
      >
        Already have a different ChainFlow account?{" "}
        <Link
          to="/login"
          className="font-semibold"
          style={{ color: "var(--gp-violet-deep)" }}
          data-testid="join-deal-to-login"
        >
          Sign in →
        </Link>
      </p>
    </Shell>
  );
}

/* ─── Page shell ─────────────────────────────────────────────────────── */
const Shell = ({ children }) => (
  <div className="min-h-screen gp-surface" data-testid="join-deal-page">
    {/* Soft ambient halo — calm, never noisy */}
    <div
      aria-hidden="true"
      className="pointer-events-none fixed top-0 left-1/2 -translate-x-1/2 w-[680px] h-[420px] rounded-full"
      style={{
        background: "radial-gradient(circle, rgba(124,58,237,0.10), transparent 70%)",
        filter: "blur(40px)",
      }}
    />
    <div className="relative mx-auto max-w-[520px] px-4 sm:px-6 pt-6 sm:pt-12 pb-10 space-y-4">
      <header className="flex items-center justify-between mb-2">
        <Link to="/" className="inline-flex items-center gap-2" data-testid="join-deal-brand">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, #6D28D9 0%, #4F46E5 100%)",
            }}
          >
            <Send size={13} className="text-white" />
          </div>
          <span className="font-display font-bold text-[14px] tracking-tight" style={{ color: "var(--gp-text)" }}>
            ChainFlow <span style={{ color: "var(--gp-text-subtle)" }}>OS</span>
          </span>
        </Link>
        <span
          className="inline-flex items-center gap-1 text-[10.5px] font-semibold"
          style={{ color: "var(--gp-text-subtle)" }}
        >
          <Users size={11} /> Operational Deal Room
        </span>
      </header>
      {children}
    </div>
  </div>
);
